import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicsHeroManager = () => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/relics/hero`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching hero data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, [fieldName]: response.data.url }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API_URL}/api/admin/relics/hero`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Hero content saved successfully!');
    } catch (error) {
      console.error('Error saving hero:', error);
      alert('Failed to save hero content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">Relics Hero Section</h1>

      <form onSubmit={handleSave} className="bg-white border rounded-lg p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">Season Badge</label>
          <input
            type="text"
            value={formData?.season_badge || ''}
            onChange={(e) => setFormData({ ...formData, season_badge: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="SEASON 1"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Title Line 1</label>
          <input
            type="text"
            value={formData?.title_line_1 || ''}
            onChange={(e) => setFormData({ ...formData, title_line_1: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="MEET THE"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Title Line 2</label>
          <input
            type="text"
            value={formData?.title_line_2 || ''}
            onChange={(e) => setFormData({ ...formData, title_line_2: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
            placeholder="RELIC PROGRAM"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Subheading</label>
          <input
            type="text"
            value={formData?.subheading || ''}
            onChange={(e) => setFormData({ ...formData, subheading: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            value={formData?.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full border rounded-lg px-3 py-2"
            rows={4}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Primary Button Text</label>
            <input
              type="text"
              value={formData?.primary_button_text || ''}
              onChange={(e) => setFormData({ ...formData, primary_button_text: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Primary Button URL</label>
            <input
              type="text"
              value={formData?.primary_button_url || ''}
              onChange={(e) => setFormData({ ...formData, primary_button_url: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Secondary Button Text</label>
            <input
              type="text"
              value={formData?.secondary_button_text || ''}
              onChange={(e) => setFormData({ ...formData, secondary_button_text: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Secondary Button URL</label>
            <input
              type="text"
              value={formData?.secondary_button_url || ''}
              onChange={(e) => setFormData({ ...formData, secondary_button_url: e.target.value })}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Hero Image (Cards)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'hero_image_url')}
            className="hidden"
            id="hero-image-upload"
          />
          <label
            htmlFor="hero-image-upload"
            className="inline-flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50"
          >
            <Upload className="w-4 h-4" />
            <span>{uploading ? 'Uploading...' : 'Upload Image'}</span>
          </label>
          {formData?.hero_image_url && (
            <img src={formData.hero_image_url} alt="Hero" className="mt-2 w-64 rounded" />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Background Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e, 'bg_image_url')}
            className="hidden"
            id="bg-image-upload"
          />
          <label
            htmlFor="bg-image-upload"
            className="inline-flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50"
          >
            <Upload className="w-4 h-4" />
            <span>{uploading ? 'Uploading...' : 'Upload Background'}</span>
          </label>
          {formData?.bg_image_url && (
            <img src={formData.bg_image_url} alt="Background" className="mt-2 w-64 rounded" />
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Hero Content'}</span>
        </button>
      </form>
    </div>
  );
};
